import { sentenceSplitter } from 'src/_common/handlers/api';
import { EditorExecCommands, PositonObj } from './editor.constants';
import { splitIntoSentences } from 'core/utils/sentenceSplitter';
import { IBaseEditor } from 'src/_common/models/quillEditor/IBaseEditor';
import { replaceQuotes } from 'core/utils';
import { CitationSpanClassName } from './editor.constants';
import { Editor } from 'tinymce';
import { getWordCount, removeNodesFromHTML } from '../helper';

export default class QuillEditorSubWrapper implements IBaseEditor {
  private editor: Editor;

  constructor(editor: Editor) {
    this.editor = editor;
  }

  getEditorWidth() {
    return this.editor.getBody().clientWidth;
  }

  getContent(contentFormat: 'text' | 'html' = 'html') {
    return this.editor.getContent({
      format: contentFormat,
    });
  }

  execCommand(command: EditorExecCommands, ...props: (boolean | undefined | any)[]) {
    this.editor.execCommand(command, ...props);
  }

  dangerouslyGetEditorLibInstance() {
    return this.editor;
  }

  /**
   * @description Appends HTML node to current cursor position.
   * @param rawHTML Raw HTML to create Node from
   * @param ignoreUndo Optional arg to ignore undo level while inserting rawHTML
   * @param retainCursorPos Optional arg to retain cursor postion while inserting rawHTML
   * @param id Required arg if using retainCursorPos
   */
  appendHTML(rawHTML: String, ignoreUndo?: boolean, retainCursorPos?: boolean, id?: string) {
    if (ignoreUndo) {
      this.editor.undoManager.ignore(() => {
        this.editor.execCommand('mceInsertContent', false, rawHTML);
      });
    } else {
      this.editor.execCommand('mceInsertContent', true, rawHTML);
    }

    if (retainCursorPos && id) {
      this.editor.selection.setCursorLocation(this.editor.dom.get(id) as Node, 0);
    }
  }

  appendHTMLAtNewLine(rawHTML: String, ignoreUndo?: boolean, retainCursorPos?: boolean, id?: string) {
    // checking if white space as a prefix is needed to be added, ie. some text is already present.
    const prefix = this.editor.selection.getNode().textContent?.trim() ? ' ' : '';
    this.editor.selection.getContent();
    this.appendHTML(`${prefix}${rawHTML}`, ignoreUndo, retainCursorPos, id);
  }

  disable() {
    this.editor.setMode('readonly');
  }

  enable() {
    this.editor.setMode('design');
  }

  // @TODO: later replace by own implementatoin
  getCharacterCount() {
    return this.editor.plugins?.wordcount?.body.getCharacterCount() || 0;
  }

  // @TODO: later replace by own implementatoin
  getCharacterCountWithoutSpaces() {
    return this.editor.plugins?.wordcount?.body.getCharacterCountWithoutSpaces() || 0;
  }

  // @TODO: later replace by own implementatoin
  getWordCount() {
    return getWordCount(this.getContent('text')) || 0;
  }

  /**
   * @description Moves the cursor to the passed postion.
   * @param {number} position Required arg, Node to place the cursor in.
   * @param {number} position Required arg, positon to move the cursor to.
   */
  setCursorPostion(node: Node, position: number) {
    this.editor.selection.setCursorLocation(node, position);
  }

  clearEditor() {
    this.editor.setContent('');
    this.editor.undoManager.clear();
    // this.editor.focus();
  }

  setContent(text: string) {
    this.editor.setContent(text);
  }

  addUndoLevel() {
    this.editor.undoManager.add();
  }

  addClass(id: string, className: string) {
    this.editor.dom.addClass(id, className);
  }

  removeNode(id: string, ignoreUndo?: boolean) {
    if (ignoreUndo) {
      this.editor.undoManager.ignore(() => {
        this.editor.execCommand('mceRemoveNode', true, this.editor.dom.get(id));
      });
    } else {
      this.editor.execCommand('mceRemoveNode', true, this.editor.dom.get(id));
    }
  }

  replaceElement(id: string, rawHTML: string, ignoreUndo?: boolean, retainCursorPos?: boolean) {
    if (ignoreUndo) {
      this.editor.undoManager.ignore(() => {
        if (this.editor.dom.get(id)) {
          this.editor.execCommand('mceSelectNode', true, this.editor.dom.get(id));
          this.editor.execCommand('mceReplaceContent', true, rawHTML);
        } else {
          this.editor.execCommand('mceInsertContent', true, rawHTML);
        }
      });
    } else {
      // add this execution in undo level
      // eslint-disable-next-line no-lonely-if
      if (this.editor.dom.get(id)) {
        this.editor.execCommand('mceSelectNode', true, this.editor.dom.get(id));
        this.editor.execCommand('mceReplaceContent', true, rawHTML);
      } else {
        this.editor.execCommand('mceInsertContent', true, rawHTML);
      }
    }
    if (retainCursorPos) {
      this.editor.selection.setCursorLocation(this.editor.dom.get(id) as Node, 0);
    }
  }

  // @TODO this is a tiny MC bug. Remove create Element
  getSelectedText(removalQueries?: string[]) {
    const preHTML = this.editor.selection?.getContent();
    return removeNodesFromHTML(preHTML, removalQueries).text;
  }

  replaceSelectedText(text: string) {
    this.editor.selection.setContent(text);
  }

  focusEditor() {
    this.editor.focus();
  }

  getContentTillCursor(sentenceLimit = 5): string {
    if (this.editor.selection.getContent({ format: 'text' })) {
      this.editor.selection.collapse();
    }
    this.editor.execCommand(
      'mceInsertContent',
      false,
      '<span id="selectionMarker"><span class="display--none">&nbsp;</span></span>'
    );

    const rng: Range = this.editor.selection.getRng() as Range;
    const preRng: Range = rng.cloneRange();
    const postRng: Range = rng.cloneRange();
    if (!this.editor.dom.select('p').length) {
      return '';
    }
    // preText processing

    preRng.setStartBefore(this.editor.getBody().querySelector('p.first') || this.editor.getBody());
    preRng.setEndAfter(this.editor.getBody().querySelector('span#selectionMarker') || this.editor.getBody());
    this.editor.selection.setRng(preRng);
    const preHTML = this.editor.selection?.getContent();
    this.editor.selection.collapse();

    this.editor.getBody()?.querySelector('span#selectionMarker')?.remove();
    // below patch required to handle prediction generation after space

    const { text: preText } = removeNodesFromHTML(preHTML, [`.${CitationSpanClassName}`, '.display--none']);

    let sentences = splitIntoSentences(replaceQuotes(preText));
    sentences = sentences.filter((sentence) => sentence.type === 'Sentence');
    let insertedCount = 0;
    const sentenceStack: string[] = [];
    for (let index = sentences.length - 1; index >= 0; index -= 1) {
      if (insertedCount < sentenceLimit) {
        if (sentences[index].raw.trim() !== '') {
          sentenceStack.push(sentences[index].raw);
          insertedCount += 1;
        }
      } else {
        break;
      }
    }

    let preInputText = '';
    // let postInputText = '';
    for (let index = 0; index < sentenceStack.length; null) {
      const element = sentenceStack.pop();
      preInputText += element;
      if (sentenceStack.length) preInputText += ' ';
    }

    return preInputText;
  }

  async getContentTillCursorUsingSentenceSplitterAPI(sentenceLimit = 4): Promise<string[]> {
    try {
      if (this.editor.selection.getContent({ format: 'text' })) {
        this.editor.selection.collapse();
      }
      this.editor.execCommand(
        'mceInsertContent',
        false,
        '<span id="selectionMarker"><span class="display--none">&nbsp;</span></span>'
      );

      const rng: Range = this.editor.selection.getRng() as Range;
      const preRng: Range = rng.cloneRange();
      const postRng: Range = rng.cloneRange();
      if (!this.editor.dom.select('p').length) {
        return [];
      }
      // preText processing

      preRng.setStartBefore(this.editor.getBody().querySelector('p.first') || this.editor.getBody());
      preRng.setEndAfter(this.editor.getBody().querySelector('span#selectionMarker') || this.editor.getBody());
      this.editor.selection.setRng(preRng);
      const preHTML = this.editor.selection?.getContent();
      this.editor.selection.collapse();

      this.editor.getBody()?.querySelector('span#selectionMarker')?.remove();
      // below patch required to handle prediction generation after space

      const { text: preText } = removeNodesFromHTML(preHTML, [`.${CitationSpanClassName}`, '.display--none']);
      const { sentences } = await sentenceSplitter(
        { text: replaceQuotes(preText).replace(/\[(\s*\S*?)\]/gm, '') },
        true
      );
      return (sentences as { text: string; start: number; end: number }[])
        .splice(-sentenceLimit)
        .map((data) => data.text);
    } catch (err) {
      return [];
    }
  }

  getContentAfterCursor(sentenceLimit = 5): string {
    if (this.editor.selection.getContent({ format: 'text' })) {
      this.editor.selection.collapse();
    }
    this.editor.execCommand(
      'mceInsertContent',
      false,
      '<span id="selectionMarker"><span class="display--none">&nbsp;</span></span>'
    );

    const rng: Range = this.editor.selection.getRng() as Range;
    const postRng: Range = rng.cloneRange();
    if (!this.editor.dom.select('p').length) {
      return '';
    }
    // preText processing

    postRng.setStartBefore(this.editor.getBody().querySelector('span#selectionMarker') || this.editor.getBody());
    postRng.setEndAfter(this.editor.getBody().querySelector('p:last-child') || this.editor.getBody());
    this.editor.selection.setRng(postRng);
    const preHTML = this.editor.selection?.getContent();
    this.editor.selection.collapse(true);

    this.editor.getBody()?.querySelector('span#selectionMarker')?.remove();
    // below patch required to handle prediction generation after space

    const { text: postText } = removeNodesFromHTML(preHTML, [`.${CitationSpanClassName}`, '.display--none']);

    let postInputText = '';

    let sentences = splitIntoSentences(replaceQuotes(postText));
    sentences = sentences.filter((sentence) => sentence.type === 'Sentence');
    let insertedCount = 0;
    const sentenceStack: string[] = [];
    for (let index = 0; index < sentences.length; index += 1) {
      if (insertedCount < sentenceLimit) {
        if (sentences[index].raw.trim() !== '') {
          postInputText += sentences[index].raw;
          insertedCount += 1;
          if (insertedCount !== sentenceLimit) postInputText += ' ';
        }
      } else {
        break;
      }
    }

    return postInputText;
  }

  getQuillheader = () => document.getElementsByTagName('header')[0] as HTMLElement;

  getToolbar = () => document.getElementsByClassName('tox-editor-header')[0];

  getOnlyOverFlownToolbar = () => document.getElementsByClassName('tox-toolbar__overflow')[0];

  getTextArea = () => this.editor.getContentAreaContainer();

  getAbsoluteCursorPosition = (currElement: HTMLElement, suggWidth: number): PositonObj => {
    // append new dummy element to get current cursor position in px
    this.appendHTML('<span id="dummySpan"><span class="display--none">&nbsp;</span></span>', true, false);
    const newTempNode = this.dangerouslyGetEditorLibInstance().dom.get('dummySpan');
    let cursorCoordinates;
    if (!newTempNode) {
      cursorCoordinates = currElement.getBoundingClientRect();
    } else {
      cursorCoordinates = newTempNode?.getBoundingClientRect();
    }
    this.editor.getBody()?.querySelector('span#dummySpan')?.remove();

    // calculate fixed height of certain elements
    const style = window.getComputedStyle(currElement, null).getPropertyValue('font-size');
    const fontSize: number = parseFloat(style) || 16;
    const editorWidth = this.getTextArea()?.offsetWidth || 0;
    const headerHeight = this.getQuillheader().clientHeight;
    const absPos = {
      writeAssist: { top: 0, left: 0, bottom: 0 },
      tooltip: { top: 0, left: 0 },
    };
    absPos.tooltip.top = cursorCoordinates?.top - fontSize;
    absPos.tooltip.left = cursorCoordinates?.left - 4;

    absPos.writeAssist.top = cursorCoordinates?.top - headerHeight + fontSize + 5;
    absPos.writeAssist.left = Math.min(cursorCoordinates?.left, editorWidth - suggWidth);
    if (absPos.writeAssist.top > (window.innerHeight * 75) / 100) {
      absPos.writeAssist.bottom = window.innerHeight - absPos.writeAssist.top - fontSize - 10;
      absPos.writeAssist.top = 0;
    }
    return absPos;
  };

  /**
   * @description Collapse the current selection and take the cursor to start/end of selection.
   * @param {boolean} start Required arg, move cursor to start or end.
   */
  collapse(start: boolean) {
    this.editor.selection.collapse(start);
  }

  getBody() {
    return this.editor.getBody();
  }

  fire(eventName: string) {
    this.editor.fire(eventName);
  }
}
